/**
 * The achievements list: every achievement as a tile, dimmed until earned,
 * with a running count. Only re-renders when the earned count changes, since
 * that is the only thing that can alter it.
 */

import { ACHIEVEMENTS } from '../data/achievements';
import type { AchievementDef } from '../data/achievements';
import type { GameState } from '../game/state';
import { bindTooltip } from './tooltip';
import { withSpeciesNames } from '../game/inventory';
import { artIdFor } from './sprites';

/** Every unearned achievement shows the same cracked egg until it hatches. */
const LOCKED_ICON = `${import.meta.env.BASE_URL}art/icons/ach/locked.png`;

/**
 * Each achievement's 48px icon, named after the id with the colon swapped
 * for a dash (public/art/icons/ach/eggs-100.png, first-triceratops.png).
 * The "first" ones are that dinosaur's art resampled to 48; the level
 * milestones reuse it with a level badge drawn by the tile. A dinosaur still
 * on a stand-in (sprites.ts) shows its stand-in's icon, as its upgrades do.
 */
function iconUrl(def: AchievementDef): string {
  const [family, key] = def.id.split(':');
  if ((family === 'level' || family === 'first') && key)
    return `${import.meta.env.BASE_URL}art/icons/ach/first-${artIdFor(key)}.png`;
  return `${import.meta.env.BASE_URL}art/icons/ach/${def.id.replace(':', '-')}.png`;
}

/** The level a milestone achievement is for, or null for the other kinds. */
function badgeText(def: AchievementDef): string | null {
  const [family, , level] = def.id.split(':');
  return family === 'level' && level ? level : null;
}

export interface AchievementsView {
  update(state: GameState): void;
}

/** The state the tiles read; set on every update so names follow evolutions. */
let viewState: GameState | null = null;
const say = (text: string): string => (viewState ? withSpeciesNames(text, viewState) : text);

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function createAchievementsView(root: HTMLElement): AchievementsView {
  // The running count sits above the grid; its height is fixed in CSS so
  // matchPanelHeights can size the panel while this tab is hidden.
  const countEl = el('p', 'achievements__count');
  const grid = el('div', 'achievements__grid');
  root.replaceChildren(countEl, grid);

  const tiles = ACHIEVEMENTS.map((def) => {
    // Icon only; the name and how to earn it live in the tooltip. Focusable
    // so keyboard users can read it too.
    const tile = el('div', 'achievement');
    tile.tabIndex = 0;
    tile.setAttribute('aria-label', 'Locked achievement');
    const icon = document.createElement('img');
    icon.className = 'achievement__icon';
    icon.alt = '';
    icon.src = LOCKED_ICON;
    tile.append(icon);
    const badge = badgeText(def);
    if (badge) tile.append(el('span', 'achievement__badge', badge));
    grid.append(tile);
    // Locked ones stay a mystery: no name or hint until earned.
    bindTooltip(tile, () =>
      tile.classList.contains('achievement--earned')
        ? {
            icon: badge ? { src: iconUrl(def), badge } : { src: iconUrl(def) },
            lines: [{ text: say(def.name) }, { text: say(def.description), muted: true }],
          }
        : {
            icon: { src: LOCKED_ICON, dim: true },
            lines: [{ text: '???' }, { text: '????????????', muted: true }],
          },
    );
    return { def, tile, icon, earned: false };
  });

  let lastCount = -1;
  let lastList: readonly string[] | null = null;

  return {
    update(state) {
      viewState = state;
      // The count, and the list itself: an imported save can have as many
      // achievements as this one, but not the same ones.
      const count = state.achievements.length;
      if (count === lastCount && state.achievements === lastList) return;
      lastCount = count;
      lastList = state.achievements;

      countEl.textContent = `${count} of ${ACHIEVEMENTS.length} earned`;
      for (const t of tiles) {
        const earned = state.achievements.includes(t.def.id);
        if (earned === t.earned) continue;
        t.earned = earned;
        t.tile.classList.toggle('achievement--earned', earned);
        t.tile.setAttribute(
          'aria-label',
          earned ? `${t.def.name}: ${t.def.description}` : 'Locked achievement',
        );
        // The real icon is revealed on earning; the egg goes back if a save is wiped.
        t.icon.src = earned ? iconUrl(t.def) : LOCKED_ICON;
      }
    },
  };
}
